import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";
import { updateUser } from "../../../core/redux/authSlice";

interface Props {
    children: React.ReactNode;
}

const RecruiterStatusGate: React.FC<Props> = ({ children }) => {
    const user = useSelector((state: any) => state.auth.user);
    const userId = useSelector((state: any) => state.auth.userId);
    const dispatch = useDispatch();

    const recruitStatus = user?.recruiterOnboarding?.recruitStatus ?? "pending";

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (user?.role === "account_manager") {
        return <>{children}</>;
    }

    const handleAgreementUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setUploadError(null);
        setUploadSuccess(false);
        try {
            const formData = new FormData();
            formData.append("file", file);
            const mediaRes = await apiInstance.post("/api/media", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const mediaId = mediaRes.data?.doc?.id;
            const userRes = await apiInstance.patch(`/api/users/${userId}`, {
                recruiterOnboarding: { signedAgreement: mediaId },
            });
            dispatch(updateUser(userRes.data?.doc));
            setUploadSuccess(true);
        } catch (err: any) {
            setUploadError(
                err?.response?.data?.errors?.[0]?.message || "Upload failed. Please try again.",
            );
        } finally {
            setUploading(false);
        }
    };

    if (recruitStatus === "active") {
        return <>{children}</>;
    }

    if (recruitStatus === "pending") {
        return (
            <div className="card border-0 bg-warning-subtle text-center py-5 px-4">
                <div className="mb-3">
                    <i className="isax isax-clock fs-48 text-warning" />
                </div>
                <h5 className="fw-semibold mb-2">Profile Under Review</h5>
                <p className="text-muted mb-0">
                    Your profile has been submitted and is awaiting approval from our team. You
                    will receive an email once your application has been reviewed.
                </p>
            </div>
        );
    }

    if (recruitStatus === "rejected") {
        return (
            <div className="card border-0 bg-danger-subtle text-center py-5 px-4">
                <div className="mb-3">
                    <i className="isax isax-close-circle fs-48 text-danger" />
                </div>
                <h5 className="fw-semibold mb-2">Registration Not Approved</h5>
                <p className="text-muted mb-0">
                    Unfortunately your registration was not approved at this time. Please
                    contact{" "}
                    <a href="mailto:admin@instaadmissions.com">admin@instaadmissions.com</a>{" "}
                    for more information.
                </p>
            </div>
        );
    }

    if (recruitStatus === "approved") {
        const hasUploaded = !!user?.recruiterOnboarding?.signedAgreement;
        return (
            <div className="card border-0 bg-info-subtle text-center py-5 px-4">
                <div className="mb-3">
                    <i className="isax isax-document-upload fs-48 text-info" />
                </div>
                <h5 className="fw-semibold mb-2">Upload Signed Agreement</h5>
                <p className="text-muted mb-3">
                    Your profile has been approved! Please sign the partnership agreement sent
                    to your email and upload it below to complete your onboarding.
                </p>
                {hasUploaded ? (
                    <div className="alert alert-success d-inline-block mx-auto">
                        <i className="isax isax-tick-circle me-2" />
                        Signed agreement uploaded successfully. Our team will review and
                        activate your account shortly.
                    </div>
                ) : (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            className="d-none"
                            onChange={handleAgreementUpload}
                        />
                        <button
                            className="btn btn-info text-white px-4"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <i className="isax isax-document-upload me-2" />
                                    Upload Signed Agreement
                                </>
                            )}
                        </button>
                        {uploadError && (
                            <p className="text-danger mt-2 mb-0 fs-14">{uploadError}</p>
                        )}
                        {uploadSuccess && (
                            <p className="text-success mt-2 mb-0 fs-14">
                                Agreement uploaded! Our team will review and activate your account.
                            </p>
                        )}
                    </>
                )}
            </div>
        );
    }

    return null;
};

export default RecruiterStatusGate;
